/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React from 'react';
import { TrackingList, Row, Column } from '../tracking-list';
import { SimpleTooltip } from '../simple-tooltip';
import SDGControlLines from './sdg-control-lines.svg';
import SDGControlStripIcon from './sdg-control-strip-icon';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { ScorecardInner } from '../../types/scorecard';
import './styles.scss';
import { tooltips } from '../impact-performance/SdgChart';
import { SDG_CODES } from '../../constants/utr';

interface SDGControlStripProps {
  heading?: string | React.JSX.Element;
  border?: boolean;
  initiativeId?: string;
  surveyId?: string;
  defaultSelectedSDGs?: any;
  className?: string;
  disabled?: boolean;
  showKeyText?: boolean;
  /** @deprecated not used? **/
  multiple?: boolean;
  displayMateriality?: boolean;
  displayTargets?: boolean;
  displayProgressBars?: boolean;
  handleClick?: (sdgCodes: number | number[] | undefined) => void;
  scorecardState: ScorecardInner;
  /** @deprecated this should be handled by parent using displayMateriality or options  **/
  isCompanyTrackerLight: boolean;
}

interface State {
  sdgCodes: number[],
}


export class SDGControlStrip extends React.Component<SDGControlStripProps, State> {

  state: State = {
    sdgCodes: [],
  }

  componentDidMount() {
    this.updateFromProps();
  }

  componentDidUpdate = (prevProps: SDGControlStripProps) => {
    if (prevProps.defaultSelectedSDGs !== this.props.defaultSelectedSDGs) {
      this.updateFromProps();
    }
  }

  updateFromProps = () => {
    if (!this.props.defaultSelectedSDGs || !Array.isArray(this.props.defaultSelectedSDGs)) {
      return;
    }

    const sdgCodes: number[] = [];
    this.props.defaultSelectedSDGs.forEach(code => sdgCodes.push(Number(code)));
    this.setState({ sdgCodes: sdgCodes });
  }

  getScorecardState = () => {
    return this.props.scorecardState;
  }

  isScorecardRequired = () => {
    const { displayMateriality = true, displayProgressBars = true } = this.props;
    return displayMateriality || displayProgressBars;
  }

  render() {
    const { initiativeId, heading, border = true, displayProgressBars = true } = this.props;

    if (this.isScorecardRequired()) {
      const scorecardState = this.getScorecardState();
      if (initiativeId && !scorecardState.loaded) {
        return <LoadingPlaceholder className='d-block background-ThemeTextWhite' height={101} />
      }
    }

    const className = `sdg-controlstrip ${this.props.className || ''}`;
    const showKey = displayProgressBars !== false && this.props.displayMateriality !== false && !!initiativeId;

    return <TrackingList className={className} border={border}>
      <Row className='py-0'>
        {!!heading ? <Column className='my-auto mx-0'>{heading}</Column> : <></>}
        <>
          {SDG_CODES
            .map(sdgCode => this.renderIcon(sdgCode, initiativeId))}
        </>
        {showKey ? <>
          <Column className='p-0 mb-0 sdg-control-line'>
            {initiativeId ? <img alt='' width='25' height='78' src={SDGControlLines} /> : null}
          </Column>
          <Column className='p-0 mb-0 pb-1 materiality-sdg-contribution'>
            {initiativeId ? this.renderKey(0, 'Materiality') : <></>}
            {initiativeId ? this.renderKey(1, 'SDG Scope & Contribution') : <></>}
          </Column>
        </> : <></>}
      </Row>
    </TrackingList>;
  }

  renderIcon(sdgCode: number, initiativeId?: string) {
    const {
      disabled,
      surveyId,
      displayTargets = true,
      displayProgressBars = true,
      displayMateriality = true,
    } = this.props;

    // Enabled and have surveyId
    const canDisplayTargets = displayTargets && !!initiativeId && !!surveyId;

    return <SDGControlStripIcon
      key={`sdg-control-strip-${sdgCode}`}
      displayProgressBars={displayProgressBars && !!initiativeId}
      displayMaterialityTachimeter={!this.props.isCompanyTrackerLight && displayMateriality && !!initiativeId}
      displayMaterialityText={displayMateriality}
      displayTargets={canDisplayTargets}
      onClick={() => this.handleToggle(sdgCode)}
      disabled={!!disabled}
      goal={this.getGoal(sdgCode)}
      materialityPc={this.getMaterialityPc(sdgCode)}
      sdgCode={sdgCode}
      selected={this.state.sdgCodes.includes(sdgCode)}
    />;
  }

  getGoal(sdgCode: number) {
    const scorecardState = this.getScorecardState();
    if (!scorecardState.loaded) {
      return {};
    }
    return scorecardState?.data?.scorecard?.goals?.[sdgCode - 1] ?? {};
  }

  getMaterialityPc(sdgCode: number): number {
    const scorecardState = this.getScorecardState();
    let materialityPc = 0;
    if (scorecardState.loaded && scorecardState.data.initiative) {
      const materialityData = scorecardState.data.initiative.materiality;
      materialityPc = materialityData ? materialityData[sdgCode] : 0;
    }
    return materialityPc;
  }

  renderKey = (k: number, text: string) => {
    let tooltip;
    switch (k) {
      case 0:
        tooltip = tooltips.MATERIALITY;
        break;
      case 1:
        tooltip = <div>
          <div>{tooltips.SCOPE}</div>
          <div>{tooltips.CONTRIBUTION}</div>
        </div>
        break;
      case 2:
        break;
      default:
        break;
    }

    return <div className='legend'>
      <SimpleTooltip text={tooltip} id={`controlstrip-${k}`}>
        <i className='fa fa-info-circle info-icon mr-1' />
      </SimpleTooltip>
      {this.props.showKeyText !== false && text}
    </div>;
  }

  handleToggle = (sdgCode: number) => {
    const { multiple, handleClick, disabled } = this.props;
    if (!!disabled) {
      return;
    }

    if (!handleClick) {
      return;
    }

    let sdgCodes = [...this.state.sdgCodes];

    const isSelected = sdgCodes.indexOf(sdgCode);

    if (!!multiple) {
      if (isSelected >= 0) {
        sdgCodes.splice(isSelected, 1);
      } else {
        sdgCodes.push(sdgCode);
      }
      handleClick(sdgCodes);
      this.setState({ sdgCodes });
      return;
    }

    if (isSelected >= 0) {
      sdgCodes = [];
      handleClick(undefined);
    } else {
      sdgCodes = [sdgCode];
      handleClick(sdgCode);
    }
    this.setState({ sdgCodes });
  }
}
